import { useState, useCallback, useRef, useEffect } from 'react';
import { streamAgentTest } from '../services/api/agent';
import type { AgentEvent, AgentTestParams, TokenUsage, ToolResultEvent, ToolCallEvent } from '../services/api/agent';

/**
 * 對話輪次結構
 */
export interface ConversationRound {
  roundNumber: number;
  userMessage: string;
  agentEvents: AgentEvent[];
  finalResponse?: string;
  totalUsage?: TokenUsage;
  isComplete: boolean;
}

interface UseAgentConversationOptions {
  selectedProviderId: string;
  selectedPresetId?: string;
  selectedNovelId?: string;
  selectedChapterIds?: string[];
  maxIterations?: number;
  temperature?: number;
  maxTokens?: number;
}

export function useAgentConversation(options: UseAgentConversationOptions) {
  const {
    selectedProviderId,
    selectedPresetId,
    selectedNovelId,
    selectedChapterIds,
    maxIterations = 10,
    temperature = 0.3,
    maxTokens = 10000,
  } = options;

  const [conversationRounds, setConversationRounds] = useState<ConversationRound[]>([]);
  const [currentRoundEvents, setCurrentRoundEvents] = useState<AgentEvent[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const hasConversation = conversationRounds.length > 0;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Build conversation history from previous rounds
  const buildConversationHistory = useCallback(() => {
    return conversationRounds.flatMap((round) => {
      const messages: Array<{ role: 'user' | 'assistant'; content: string }> = [
        { role: 'user', content: round.userMessage },
      ];

      let assistantContent = round.finalResponse || '';

      if (!assistantContent) {
        const toolResults = round.agentEvents.filter(
          (e): e is ToolResultEvent => e.type === 'tool_result'
        );
        if (toolResults.length > 0) {
          assistantContent = toolResults
            .map((tr) =>
              tr.result.success
                ? `[${tr.toolName}] ${tr.result.content.substring(0, 500)}`
                : `[${tr.toolName}] Error: ${tr.result.error}`
            )
            .join('\n');
        }
      }

      if (!assistantContent) {
        const toolCalls = round.agentEvents.filter(
          (e): e is ToolCallEvent => e.type === 'tool_call'
        );
        if (toolCalls.length > 0) {
          assistantContent = toolCalls
            .map((tc) => `[Called tool: ${tc.toolName}]`)
            .join(', ');
        }
      }

      if (!assistantContent) {
        const completeEvent = round.agentEvents.find((e) => e.type === 'complete');
        if (completeEvent && 'content' in completeEvent) {
          assistantContent = (completeEvent as { content: string }).content || '';
        }
      }

      if (!assistantContent) {
        assistantContent = '[Agent completed this round]';
      }

      messages.push({ role: 'assistant', content: assistantContent });
      return messages;
    });
  }, [conversationRounds]);

  // Execute Agent test
  const handleExecute = useCallback(async (messageToSend: string) => {
    if (!selectedProviderId) {
      setError('請選擇 Provider');
      return;
    }
    if (!messageToSend.trim()) {
      setError('請輸入訊息');
      return;
    }

    setError(null);
    setCurrentRoundEvents([]);
    setIsExecuting(true);

    abortControllerRef.current = new AbortController();
    const roundNumber = conversationRounds.length + 1;

    try {
      const params: AgentTestParams = {
        provider_id: selectedProviderId,
        jailbreak_preset_id: selectedPresetId || undefined,
        user_message: messageToSend,
        novel_id: selectedNovelId || undefined,
        chapter_ids: selectedChapterIds && selectedChapterIds.length > 0 ? selectedChapterIds : undefined,
        max_iterations: maxIterations,
        temperature,
        max_tokens: maxTokens,
        conversation_history: hasConversation ? buildConversationHistory() : undefined,
      };

      let collectedEvents: AgentEvent[] = [];
      let finalResponse = '';
      let totalUsage: TokenUsage | undefined;

      await streamAgentTest(
        params,
        {
          onEvent: (event) => {
            collectedEvents = [...collectedEvents, event];
            setCurrentRoundEvents([...collectedEvents]);

            if (event.type === 'complete') {
              finalResponse = event.content;
              totalUsage = event.totalUsage;
            }
          },
          onError: (err) => setError(err.message),
          onComplete: () => {
            const newRound: ConversationRound = {
              roundNumber,
              userMessage: messageToSend,
              agentEvents: collectedEvents,
              finalResponse,
              totalUsage,
              isComplete: true,
            };
            setConversationRounds((prev) => [...prev, newRound]);
            setCurrentRoundEvents([]);
            setIsExecuting(false);
          },
        },
        abortControllerRef.current.signal
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Test failed');
      }
      setIsExecuting(false);
      abortControllerRef.current = null;
    }
  }, [
    selectedProviderId, selectedPresetId, selectedNovelId, selectedChapterIds,
    maxIterations, temperature, maxTokens, hasConversation, buildConversationHistory,
    conversationRounds.length,
  ]);

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsExecuting(false);
    }
  }, []);

  const handleClear = useCallback(() => {
    setConversationRounds([]);
    setCurrentRoundEvents([]);
    setError(null);
  }, []);

  const totalTokenUsage = conversationRounds.reduce(
    (acc, round) => ({
      inputTokens: acc.inputTokens + (round.totalUsage?.inputTokens || 0),
      outputTokens: acc.outputTokens + (round.totalUsage?.outputTokens || 0),
      totalTokens: acc.totalTokens + (round.totalUsage?.totalTokens || 0),
    }),
    { inputTokens: 0, outputTokens: 0, totalTokens: 0 }
  );

  return {
    conversationRounds,
    currentRoundEvents,
    isExecuting,
    error,
    hasConversation,
    totalTokenUsage,
    handleExecute,
    handleStop,
    handleClear,
  };
}
